import { useLLMUsagesActions } from '@hooks/llmUsages/useLLMUsagesActions';
import type { DataValidationModalProps } from '@interfaces/TDataSourceGeneration';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const DataValidationContent: React.FC<DataValidationModalProps> = ({
  selectedWeeks,
  selectedYear,
  configId,
  llmBlockId,
  hotTopicId,
  conversations,
  setConversations,
}) => {
  const { t } = useTranslation();
  const { getDataForDataSourceGeneration } = useLLMUsagesActions();
  const [noFactCheckedData, setNoFactCheckedData] = useState<boolean>(false);
  // Track in-flight state so status lines are visible immediately on mount,
  // before the (potentially slow) API response arrives.
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // Only mount the details panel once the preceding animated lines have
  // finished, so the scrollbar never appears on opacity-0 layout content.
  const [showDetails, setShowDetails] = useState<boolean>(false);

  const fetchData = useCallback(async () => {
    const data = await getDataForDataSourceGeneration(
      selectedWeeks,
      configId,
      llmBlockId,
      selectedYear,
      hotTopicId,
    );
    setConversations(data);
    setIsLoading(false);
  }, [
    selectedWeeks,
    selectedYear,
    configId,
    llmBlockId,
    hotTopicId,
    getDataForDataSourceGeneration,
    setConversations,
  ]);

  const startAnimation = useCallback(
    (timeoutSec = 0.5, optionalClass?: string) => {
      const elements = document.querySelectorAll(
        `.animated-element${optionalClass ? `.${optionalClass}` : ''}`,
      );
      elements.forEach((element, elementIndex) => {
        setTimeout(
          () => {
            element.classList.add('animate');
            (element as HTMLElement).style.animationDelay =
              `${(elementIndex + 1) * timeoutSec}s`;
          },
          (elementIndex + 1) * timeoutSec * 1000,
        );
      });
    },
    [],
  );

  useEffect(() => {
    if (!conversations) {
      return;
    }

    if (conversations?.factCheckedData?.length) {
      startAnimation();

      // Count the animated-element lines that will actually render so the
      // details panel appears only after all of them have become visible.
      const animatedLineCount =
        2 + // hyperlink-completed + hyperlink-results (always present)
        (conversations.hyperlinksCorrectedCount > 0 ? 1 : 0) +
        2 + // duplicate-completed + duplicate-results (always present)
        (conversations.duplicateQuestions.length > 0 ? 1 : 0);

      const detailsDelayMs = (animatedLineCount + 1) * 500 + 300;
      setTimeout(() => {
        setShowDetails(true);
      }, detailsDelayMs);
    } else {
      setNoFactCheckedData(true);
      // should only display the footer animation if there is no fact checked data
      startAnimation(0, 'tailwind-modal-footer');
      setShowDetails(true);
    }
  }, [conversations, startAnimation]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: it should run only once
  useEffect(() => {
    fetchData();
  }, []);

  // Whether there is data to display results for; drives the transition from
  // "in progress" lines to the animated completion/results lines.
  const hasData =
    !isLoading && conversations && conversations.factCheckedData?.length > 0;

  return (
    <div className='data-validation h-full flex flex-col rounded-lg bg-gray-100 overflow-hidden border border-primary-dark'>
      <div className='flex bg-primary-dark text-white text-base font-normal py-1 px-3'>
        {t('train-ai.data-source-generation.data-validation')}
      </div>

      {/* Validation starting — shown immediately on mount so the user sees activity while the API call is in-flight */}
      <div className='flex font-mono text-pico text-white bg-primary-base py-2 px-3 min-h-[32px]'>
        {(isLoading || hasData) && (
          <># {t('train-ai.data-source-generation.validation-starting')}</>
        )}
      </div>

      {/* Hyperlink syntax */}
      <div className='flex py-1 px-3 bg-blue-periwinkle text-white text-sm font-normal'>
        {t('train-ai.data-source-generation.hyperlink-syntax')}
      </div>

      {/* Hyperlink syntax status lines */}
      <div className='flex flex-col font-mono text-pico text-white bg-primary-base py-2 px-3 gap-0.5 min-h-[86px]'>
        {/* "Checking..." shown immediately; stays visible while results animate in */}
        {(isLoading || hasData) && (
          <span>
            {`# ${t('train-ai.data-source-generation.checking-hyperlink-syntax')}`}
          </span>
        )}
        {hasData && (
          <>
            {conversations?.hyperlinksCorrectedCount > 0 && (
              <span className='animated-element'>
                {`# ${t('train-ai.data-source-generation.correcting-hyperlink-syntax')}`}
              </span>
            )}

            <span className='animated-element'>
              {`# ${t('train-ai.data-source-generation.hyperlink-checking-completed')}`}
            </span>
            <span className='animated-element'>
              {`- ${t('train-ai.data-source-generation.hyperlink-syntax-results', { value: conversations?.hyperlinksCorrectedCount ?? 0 })}`}
            </span>
          </>
        )}
      </div>

      {/* Duplicate questions */}
      <div className='flex py-1 px-3 bg-blue-periwinkle text-white text-sm font-normal'>
        {t('train-ai.data-source-generation.duplicate-questions')}
      </div>

      {/* Duplicate questions status lines */}
      <div className='flex flex-col font-mono text-pico text-white bg-primary-base py-2 px-3 gap-0.5 min-h-[86px]'>
        {/* "Checking..." shown immediately; stays visible while results animate in */}
        {(isLoading || hasData) && (
          <span>
            {`# ${t('train-ai.data-source-generation.checking-duplicate-questions')}`}
          </span>
        )}
        {hasData && (
          <>
            {conversations?.duplicateQuestions?.length > 0 && (
              <span className='animated-element'>
                {`# ${t('train-ai.data-source-generation.found-duplicate-questions')}`}
              </span>
            )}
            <span className='animated-element'>
              {`# ${t('train-ai.data-source-generation.duplicate-questions-checking-completed')}`}
            </span>
            <span className='animated-element'>
              {`- ${t('train-ai.data-source-generation.duplicate-questions-results', { value: conversations?.duplicateQuestions?.length ?? 0 })}`}
            </span>
          </>
        )}
      </div>

      {/* Details on duplicate questions — only mounted once the preceding
          animated lines are visible; prevents scrollbar on opacity-0 content */}
      <div className='h-full flex flex-col py-2 px-3 bg-gray-100 text-primary-base text-tiny font-normal overflow-y-auto'>
        {showDetails &&
          conversations &&
          conversations.duplicateQuestions.length > 0 && (
            <div className='flex flex-col'>
              <div className='font-medium text-primary-dark mb-2'>
                {t(
                  'train-ai.data-source-generation.details-on-duplicate-questions',
                )}
              </div>
              <div className='flex flex-col gap-1.5'>
                {conversations.duplicateQuestions.map(
                  (duplicateQuestion, questionIndex) => (
                    <div key={questionIndex} className='flex flex-col'>
                      <span className='italic text-tiny'>
                        {duplicateQuestion.question}
                      </span>
                      <span className='text-pico'>
                        {t(
                          'train-ai.data-source-generation.duplicate-questions.indexes',
                        )}
                        :{' '}
                        {duplicateQuestion.indexes.map((occurrence) => {
                          const isLast =
                            occurrence ===
                            duplicateQuestion.indexes[
                              duplicateQuestion.indexes.length - 1
                            ];
                          return `W${String(occurrence.week).padStart(2, '0')}/${occurrence.index}${isLast ? '' : ', '}`;
                        })}{' '}
                        -{' '}
                        {t(
                          'train-ai.data-source-generation.duplicate-questions.total',
                        )}
                        : {duplicateQuestion.indexes.length}
                      </span>
                    </div>
                  ),
                )}
              </div>
            </div>
          )}
        {noFactCheckedData && (
          <div className='h-full flex p-3 items-center justify-center text-primary-base font-normal text-sm text-center'>
            {t('train-ai.data-source-generation.no-fact-checked-data')}
          </div>
        )}
      </div>
    </div>
  );
};

export default DataValidationContent;
